import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CreditCard, CheckCircle, Copy } from "lucide-react";
import toast from "react-hot-toast";

export default function EbookPaymentModal({ book, open, onClose }) {
    const { user } = useAuth();
    const nav = useNavigate();

    const [payInfo, setPayInfo] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [txnId, setTxnId] = useState("");
    const [senderNumber, setSenderNumber] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!open) return;
        setDone(false);
        setTxnId("");
        setSenderNumber("");
        setSelectedPlan(null);
        api.get("/ebook-payment-info")
            .then((r) => setPayInfo(r.data))
            .catch(() => {});
    }, [open]);

    const plans = book?.subscription_plans || [];

    const hasNagad = !!payInfo?.nagad_number;
    const hasBkash = !!payInfo?.bkash_number;

    const copyNumber = (num) => {
        navigator.clipboard.writeText(num).then(() => toast.success("নম্বর কপি হয়েছে!"));
    };

    const handleSubmit = async () => {
        if (!user) {
            toast.error("পেমেন্ট করতে আগে লগইন করুন");
            nav("/login");
            return;
        }
        if (!txnId.trim()) {
            toast.error("Transaction ID দিন");
            return;
        }
        if (!senderNumber.trim()) {
            toast.error("আপনার পেমেন্ট নম্বর দিন");
            return;
        }
        if (!selectedPlan) {
            toast.error("প্ল্যান সিলেক্ট করুন");
            return;
        }

        setSubmitting(true);
        try {
            await api.post("/ebook/subscribe", {
                book_id: book.id,
                plan_days: selectedPlan.days,
                txn_id: txnId.trim(),
                sender_number: senderNumber.trim(),
            });
            setDone(true);
        } catch (err) {
            const msg = err?.response?.data?.detail || "কিছু একটা সমস্যা হয়েছে";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg w-full sm:w-full z-[100] font-bn p-3 sm:p-6">
                <DialogHeader>
                    <DialogTitle className="text-brand-navy text-base sm:text-xl flex items-center gap-2">
                        <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-brand-royal shrink-0" />
                        <span>ই-বুক সাবস্ক্রিপশন</span>
                    </DialogTitle>
                </DialogHeader>

                {done ? (
                    <div className="text-center py-4 sm:py-8 space-y-3 sm:space-y-4">
                        <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mx-auto" />
                        <h3 className="text-base sm:text-xl font-bold text-brand-navy">
                            পেমেন্ট রিকোয়েস্ট পাঠানো হয়েছে!
                        </h3>
                        <p className="text-brand-navy/70 text-xs sm:text-sm leading-relaxed">
                            আপনার পেমেন্ট ভেরিফাই হওয়ার পর অ্যাডমিন আপনার সাবস্ক্রিপশন সক্রিয়
                            করবেন। সাধারণত <strong>১-২ ঘণ্টার</strong> মধ্যে সক্রিয় হয়।
                        </p>
                        <p className="text-brand-navy/60 text-[11px] sm:text-xs">
                            আপনার প্রোফাইলের <strong>"আমার ই-বুক"</strong> ট্যাবে স্ট্যাটাস দেখতে
                            পারবেন।
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center mt-2">
                            <button
                                onClick={() => nav("/profile")}
                                className="btn-primary px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold w-full sm:w-auto"
                            >
                                প্রোফাইল দেখুন
                            </button>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl border border-brand-navy/30 text-brand-navy text-xs sm:text-sm font-semibold hover:bg-slate-50 w-full sm:w-auto"
                            >
                                বন্ধ করুন
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 sm:space-y-5">
                        <div className="flex items-center gap-2 sm:gap-3 bg-brand-light/30 rounded-xl p-2 sm:p-3">
                            {book.cover_image && (
                                <img
                                    src={
                                        book.cover_image.startsWith("/")
                                            ? `${BACKEND_URL}${book.cover_image}`
                                            : book.cover_image
                                    }
                                    alt={book.title}
                                    className="w-8 h-11 sm:w-12 sm:h-16 object-cover rounded-lg shadow shrink-0"
                                />
                            )}
                            <div className="min-w-0">
                                <div className="font-bold text-brand-navy text-xs sm:text-sm truncate">{book.title}</div>
                                <div className="text-[11px] sm:text-xs text-brand-navy/60 truncate">{book.author}</div>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs sm:text-sm font-bold text-brand-navy mb-1.5 sm:mb-2">
                                ১. প্ল্যান বেছে নিন
                            </p>
                            <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                                {plans.map((plan) => (
                                    <button
                                        key={plan.days}
                                        onClick={() => setSelectedPlan(plan)}
                                        className={`rounded-xl border-2 p-2 sm:p-3 text-center transition-all ${
                                            selectedPlan?.days === plan.days
                                                ? "border-brand-royal bg-brand-royal/10"
                                                : "border-slate-200 hover:border-brand-royal/50"
                                        }`}
                                    >
                                        <div className="text-xs sm:text-base font-bold text-brand-navy">
                                            {plan.label}
                                        </div>
                                        <div className="text-sm sm:text-lg font-extrabold text-brand-royal">
                                            ৳{plan.price}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-2.5 sm:p-4 space-y-2 sm:space-y-3">
                            <p className="text-xs sm:text-sm font-bold text-brand-navy">
                                ২. নিচের নম্বরে{" "}
                                <span className="text-brand-royal">
                                    {selectedPlan ? `৳${selectedPlan.price}` : "নির্বাচিত মূল্য"}
                                </span>{" "}
                                Send Money করুন
                            </p>
                            {hasBkash && (
                                <div className="flex items-center justify-between bg-white rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 border border-pink-200">
                                    <div className="min-w-0">
                                        <span className="text-[11px] sm:text-xs font-semibold text-pink-600">bKash</span>
                                        <div className="font-bold text-brand-navy text-xs sm:text-sm truncate">{payInfo.bkash_number}</div>
                                    </div>
                                    <button
                                        onClick={() => copyNumber(payInfo.bkash_number)}
                                        className="text-slate-400 hover:text-brand-royal transition shrink-0 ml-2"
                                    >
                                        <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                </div>
                            )}
                            {hasNagad && (
                                <div className="flex items-center justify-between bg-white rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 border border-orange-200">
                                    <div className="min-w-0">
                                        <span className="text-[11px] sm:text-xs font-semibold text-orange-600">Nagad</span>
                                        <div className="font-bold text-brand-navy text-xs sm:text-sm truncate">{payInfo.nagad_number}</div>
                                    </div>
                                    <button
                                        onClick={() => copyNumber(payInfo.nagad_number)}
                                        className="text-slate-400 hover:text-brand-royal transition shrink-0 ml-2"
                                    >
                                        <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                </div>
                            )}
                            {payInfo?.rocket_number && (
                                <div className="flex items-center justify-between bg-white rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 border border-purple-200">
                                    <div className="min-w-0">
                                        <span className="text-[11px] sm:text-xs font-semibold text-purple-600">Rocket</span>
                                        <div className="font-bold text-brand-navy text-xs sm:text-sm truncate">{payInfo.rocket_number}</div>
                                    </div>
                                    <button
                                        onClick={() => copyNumber(payInfo.rocket_number)}
                                        className="text-slate-400 hover:text-brand-royal transition shrink-0 ml-2"
                                    >
                                        <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                </div>
                            )}
                            {payInfo?.instructions && (
                                <p className="text-[11px] sm:text-xs text-brand-navy/60 mt-1">{payInfo.instructions}</p>
                            )}
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                            <p className="text-xs sm:text-sm font-bold text-brand-navy">৩. পেমেন্টের তথ্য দিন</p>
                            <div>
                                <label className="block text-[11px] sm:text-xs font-semibold text-brand-navy/70 mb-1">
                                    Transaction ID (TxnID / Ref No)
                                </label>
                                <input
                                    type="text"
                                    value={txnId}
                                    onChange={(e) => setTxnId(e.target.value)}
                                    placeholder="যেমন: 8N7ABC12XY"
                                    className="w-full px-3 py-2 sm:py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-royal/30 focus:border-brand-royal"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] sm:text-xs font-semibold text-brand-navy/70 mb-1">
                                    আপনার বিকাশ/নগদ নম্বর (Sender)
                                </label>
                                <input
                                    type="tel"
                                    value={senderNumber}
                                    onChange={(e) => setSenderNumber(e.target.value)}
                                    placeholder="01XXXXXXXXX"
                                    className="w-full px-3 py-2 sm:py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-royal/30 focus:border-brand-royal"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={submitting || !selectedPlan || !txnId.trim() || !senderNumber.trim()}
                            className="btn-accent w-full py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="60" strokeDashoffset="15" />
                                    </svg>
                                    পাঠানো হচ্ছে...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    পেমেন্ট কনফার্ম করুন
                                </>
                            )}
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
